import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import type { ComparisonData } from '../App';
interface CustomerComparisonProps {
  data: ComparisonData;
  onBack: () => void;
}
function fmt(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
export function CustomerComparison({
  data,
  onBack
}: CustomerComparisonProps) {
  const {
    productLine,
    selectedFrameColor,
    selectedExteriorColor,
    grandTotal,
    totalDue,
    totalSqFt,
    synchronyOption
  } = data;
  const andersenRetail = grandTotal * 1.24;
  const pellaRetail = grandTotal * 1.3;
  const relentlessRetail = grandTotal;
  const energyStarCredit = Math.min(totalDue * 0.3, 600);
  const srpRebate = totalSqFt * 3;
  const totalIncentives = energyStarCredit + srpRebate;
  const monthlyPayment = synchronyOption ? totalDue * synchronyOption.monthlyFactor : 0;
  return <div className="min-h-screen w-full bg-gray-50" data-id="element-384">
      <header className="bg-white border-b border-gray-200 shadow-sm" data-id="element-385">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-386">
          <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-id="element-387">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-388" />
            Back to Calculator
          </button>
          <h1 className="text-xl font-bold text-gray-900" data-id="element-389">
            Window Pricing Comparison
          </h1>
          <div className="w-24" data-id="element-390"></div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-391">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-392">
          {/* Competitor Logos */}
          <div className="grid grid-cols-3 border-b border-gray-200" data-id="element-393">
            <div className="p-6 border-r border-gray-200 flex items-center justify-center bg-gray-50" data-id="element-394">
              <div className="text-center" data-id="element-395">
                <div className="text-lg font-bold text-gray-900" data-id="element-396">RENEWAL</div>
                <div className="text-sm text-gray-600" data-id="element-397">by ANDERSEN</div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-398">
                  FULL SERVICE WINDOW & DOOR REPLACEMENT
                </div>
              </div>
            </div>
            <div className="p-6 border-r border-gray-200 flex items-center justify-center bg-amber-50" data-id="element-399">
              <div className="text-center" data-id="element-400">
                <div className="text-2xl font-bold text-amber-600" data-id="element-401">Pella</div>
                <div className="text-sm text-gray-700 mt-1" data-id="element-402">
                  WINDOWS & DOORS
                </div>
              </div>
            </div>
            <div className="p-6 flex items-center justify-center bg-emerald-50" data-id="element-403">
              <div className="text-center" data-id="element-404">
                <div className="text-xl font-bold text-emerald-700" data-id="element-405">
                  RELENTLESS
                </div>
                <div className="text-sm text-emerald-600" data-id="element-406">CONSTRUCTION</div>
              </div>
            </div>
          </div>

          {/* Estimated Retail Cost */}
          <div className="grid grid-cols-3 border-b border-gray-200" data-id="element-407">
            <div className="p-6 border-r border-gray-200" data-id="element-408">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-409">
                *Estimated Retail Cost
              </div>
              <div className="text-3xl font-bold text-gray-900" data-id="element-410">
                {fmt(andersenRetail)}
              </div>
            </div>
            <div className="p-6 border-r border-gray-200" data-id="element-411">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-412">
                *Estimated Retail Cost
              </div>
              <div className="text-3xl font-bold text-gray-900" data-id="element-413">
                {fmt(pellaRetail)}
              </div>
            </div>
            <div className="p-6" data-id="element-414">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-415">
                *Estimated Retail Cost
              </div>
              <div className="text-3xl font-bold text-gray-900" data-id="element-416">
                {fmt(relentlessRetail)}
              </div>
            </div>
          </div>

          {/* Advertisement Home Price */}
          <div className="grid grid-cols-3 border-b border-gray-200" data-id="element-417">
            <div className="p-6 border-r border-gray-200" data-id="element-418"></div>
            <div className="p-6 border-r border-gray-200" data-id="element-419"></div>
            <div className="p-6 bg-emerald-50" data-id="element-420">
              <div className="text-lg font-semibold text-emerald-700 mb-2" data-id="element-421">
                Advertisement Home Price
              </div>
              <div className="text-2xl font-bold text-emerald-900" data-id="element-422">
                {fmt(totalDue)}
              </div>
            </div>
          </div>

          {/* Financing Section */}
          {synchronyOption && <div className="grid grid-cols-3 border-b border-gray-200" data-id="element-423">
              <div className="p-6 border-r border-gray-200" data-id="element-424"></div>
              <div className="p-6 border-r border-gray-200" data-id="element-425"></div>
              <div className="p-6 bg-blue-50" data-id="element-426">
                <div className="text-sm font-semibold text-gray-700 mb-3" data-id="element-427">
                  Financing (based on credit)
                </div>
                <div className="space-y-2" data-id="element-428">
                  <div className="flex justify-between items-center" data-id="element-429">
                    <span className="text-sm text-gray-600" data-id="element-430">Finance Rate</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-431">
                      {synchronyOption.interestRate}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-432">
                    <span className="text-sm text-gray-600" data-id="element-433">Term</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-434">
                      {synchronyOption.termMonths}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-435">
                    <span className="text-sm text-gray-600" data-id="element-436">Monthly Rate</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-437">
                      {fmt(monthlyPayment)}
                    </span>
                  </div>
                </div>
              </div>
            </div>}

          {/* Incentives */}
          <div className="grid grid-cols-3 border-b border-gray-200" data-id="element-438">
            <div className="p-6 border-r border-gray-200" data-id="element-439">
              <div className="text-sm italic text-gray-700" data-id="element-440">
                Energy Star tax credit
              </div>
            </div>
            <div className="p-6 border-r border-gray-200 bg-yellow-100" data-id="element-441">
              <div className="text-xl font-bold text-gray-900" data-id="element-442">
                {fmt(energyStarCredit)}
              </div>
            </div>
            <div className="p-6" data-id="element-443"></div>
          </div>

          <div className="grid grid-cols-3 border-b border-gray-200" data-id="element-444">
            <div className="p-6 border-r border-gray-200" data-id="element-445">
              <div className="text-sm italic text-gray-700" data-id="element-446">
                SRP Rebate (if applicable)
              </div>
            </div>
            <div className="p-6 border-r border-gray-200 bg-yellow-100" data-id="element-447">
              <div className="text-xl font-bold text-gray-900" data-id="element-448">
                {fmt(srpRebate)}
              </div>
            </div>
            <div className="p-6" data-id="element-449"></div>
          </div>

          <div className="grid grid-cols-3" data-id="element-450">
            <div className="p-6 border-r border-gray-200" data-id="element-451">
              <div className="text-sm font-semibold italic text-gray-800" data-id="element-452">
                Total Incentives
              </div>
            </div>
            <div className="p-6 border-r border-gray-200 bg-yellow-100" data-id="element-453">
              <div className="text-xl font-bold text-gray-900" data-id="element-454">
                {fmt(totalIncentives)}
              </div>
            </div>
            <div className="p-6" data-id="element-455"></div>
          </div>
        </div>

        {/* Quote Details */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-456">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-457">
            Quote Details
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-458">
            <div data-id="element-459">
              <span className="text-gray-600" data-id="element-460">Product Line:</span>
              <span className="ml-2 font-medium text-gray-900" data-id="element-461">
                {productLine}
              </span>
            </div>
            <div data-id="element-462">
              <span className="text-gray-600" data-id="element-463">Frame Color:</span>
              <span className="ml-2 font-medium text-gray-900" data-id="element-464">
                {selectedFrameColor}
              </span>
            </div>
            {selectedExteriorColor !== 'None' && <div data-id="element-465">
                <span className="text-gray-600" data-id="element-466">Exterior Color:</span>
                <span className="ml-2 font-medium text-gray-900" data-id="element-467">
                  {selectedExteriorColor}
                </span>
              </div>}
            <div data-id="element-468">
              <span className="text-gray-600" data-id="element-469">Total Square Footage:</span>
              <span className="ml-2 font-medium text-gray-900" data-id="element-470">
                {totalSqFt.toFixed(1)} sq ft
              </span>
            </div>
            {synchronyOption && <div data-id="element-471">
                <span className="text-gray-600" data-id="element-472">Financing Plan:</span>
                <span className="ml-2 font-medium text-gray-900" data-id="element-473">
                  {synchronyOption.name}
                </span>
              </div>}
          </div>
        </div>

        <div className="mt-4 text-center" data-id="element-474">
          <p className="text-xs text-gray-400 italic" data-id="element-475">
            *Estimated retail costs are approximate comparisons based on
            industry pricing.
          </p>
        </div>
      </main>
    </div>;
}